import React from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Select, Upload, Space, Tag, Empty } from 'antd';
import { FileOutlined, DownloadOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useIsMobile } from '../hooks/useIsMobile';

interface Document {
  id: number;
  documentType: string;
  description: string;
  uploadDate: string;
  uploadedBy: string;
  size: number;
  fileName: string;
}

const DocumentFlow: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [typeFilter, setTypeFilter] = React.useState<string | undefined>(undefined);
  const [uploadVisible, setUploadVisible] = React.useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      // Документооборот файлов должен жить на бэкенде.
      // Раньше здесь были тестовые документы в localStorage — убрано для продакшена.
      const stored = localStorage.getItem('document_flow');
      if (stored) {
        const parsed = JSON.parse(stored);
        setDocuments(Array.isArray(parsed) ? parsed : []);
      } else {
        setDocuments([]);
      }
    } catch (e: any) {
      message.error('Ошибка загрузки документов');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      const newDoc: Document = {
        id: Math.max(...documents.map(d => d.id), 0) + 1,
        documentType: values.documentType,
        description: values.description,
        uploadDate: new Date().toISOString(),
        uploadedBy: 'admin',
        size: Math.floor(Math.random() * 5000000),
        fileName: `document_${Date.now()}.pdf`
      };
      
      const updated = [...documents, newDoc];
      setDocuments(updated);
      localStorage.setItem('document_flow', JSON.stringify(updated));
      message.success('Документ загружен');
      setUploadVisible(false);
      form.resetFields();
    } catch (e: any) {
      message.error('Ошибка при загрузке документа');
    }
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Удалить документ?',
      content: 'Это действие нельзя отменить',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: () => {
        const updated = documents.filter(d => d.id !== id);
        setDocuments(updated);
        localStorage.setItem('document_flow', JSON.stringify(updated));
        message.success('Документ удален');
      }
    });
  };

  const getTypeTag = (type: string) => {
    const colors: { [key: string]: string } = {
      agreement: 'blue',
      invoice: 'green',
      specification: 'orange',
      act: 'purple'
    };
    const labels: { [key: string]: string } = {
      agreement: 'Контракт',
      invoice: 'Счет-фактура',
      specification: 'Спецификация',
      act: 'Акт'
    };
    return <Tag color={colors[type]}>{labels[type]}</Tag>;
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 Б';
    const k = 1024;
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredDocuments = typeFilter
    ? documents.filter(d => d.documentType === typeFilter)
    : documents;

  const columns = [
    {
      title: 'Тип',
      dataIndex: 'documentType',
      key: 'documentType',
      width: 110,
      render: (type: string) => getTypeTag(type),
      sorter: (a: Document, b: Document) => a.documentType.localeCompare(b.documentType)
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      sorter: (a: Document, b: Document) => a.description.localeCompare(b.description)
    },
    {
      title: 'Файл',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 140,
      render: (name: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FileOutlined style={{ color: '#1890ff' }} />
          <span style={{ fontSize: '13px' }}>{name}</span>
        </div>
      )
    },
    {
      title: 'Размер',
      dataIndex: 'size',
      key: 'size',
      width: 90,
      render: (size: number) => (
        <span style={{ fontSize: '13px', color: '#666' }}>{formatSize(size)}</span>
      )
    },
    {
      title: 'Загружен',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      width: 100,
      render: (name: string) => (
        <span style={{ fontSize: '13px' }}>{name}</span>
      )
    },
    {
      title: 'Дата',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      width: 130,
      render: (date: string) => (
        <span style={{ fontSize: '13px', color: '#666' }}>
          {new Date(date).toLocaleDateString('ru')}
        </span>
      ),
      sorter: (a: Document, b: Document) => new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime()
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: Document) => (
        <Space size="small" wrap>
          <Button
            size="small"
            type="primary"
            ghost
            icon={<DownloadOutlined />}
            onClick={() => message.info('Загрузка: ' + record.fileName)}
            style={{ fontSize: '12px' }}
          >
            Скачать
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      )
    }
  ];

  return (
    
      <Card
        title="Документооборот"
        loading={loading}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setUploadVisible(true)}
            block={isMobile}
          >
            {isMobile ? 'Загрузить' : 'Загрузить документ'}
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Фильтр по типу */}
          <div style={{ maxWidth: 300 }}>
            <Select
              placeholder="Фильтр по типу"
              allowClear
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: '100%' }}
              options={[
                { label: 'Контракты', value: 'agreement' },
                { label: 'Счета-фактуры', value: 'invoice' },
                { label: 'Спецификации', value: 'specification' },
                { label: 'Акты', value: 'act' }
              ]}
            />
          </div>

          {/* Таблица */}
          {filteredDocuments.length === 0 ? (
            <Empty description="Документов не найдено" />
          ) : (
            <Table
              rowKey="id"
              columns={columns as any}
              dataSource={filteredDocuments}
              pagination={{ pageSize: 10 }}
              size={isMobile ? 'small' : 'middle'}
              scroll={{ x: isMobile ? 'max-content' : undefined }}
              locale={{ emptyText: 'Документы не найдены' }}
            />
          )}
        </Space>

        {/* Модал загрузки */}
        <Modal
          title="Загрузить документ"
          open={uploadVisible}
          onCancel={() => {
            setUploadVisible(false);
            form.resetFields();
          }}
          okText="Загрузить"
          cancelText="Отмена"
          onOk={handleUpload}
          width={isMobile ? '100%' : 520}
          centered={!isMobile}
        >
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Тип документа"
              name="documentType"
              rules={[{ required: true, message: 'Выберите тип' }]}
            >
              <Select
                placeholder="Выберите тип документа"
                options={[
                  { label: 'Контракт', value: 'agreement' },
                  { label: 'Счет-фактура', value: 'invoice' },
                  { label: 'Спецификация', value: 'specification' },
                  { label: 'Акт', value: 'act' }
                ]}
              />
            </Form.Item>
            <Form.Item
              label="Описание"
              name="description"
              rules={[{ required: true, message: 'Введите описание' }]}
            >
              <Input.TextArea rows={3} placeholder="Описание документа" />
            </Form.Item>
            <Form.Item label="Файл">
              <Upload
                maxCount={1}
                accept=".pdf,.xlsx,.doc,.docx"
              >
                <Button icon={<UploadOutlined />}>Выбрать файл</Button>
              </Upload>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    
  );
};

export default DocumentFlow;
